/**
 * Country-aware phone formatting and validation.
 * No external dependencies — pure string manipulation.
 */

export type PhoneCountry = 'brazil' | 'canada' | 'us';

/**
 * Resolve which phone-formatting country to use.
 * Priority: explicit country string > currency fallback > default (brazil).
 */
export function resolvePhoneCountry(
    country?: string | null,
    currency?: string | null
): PhoneCountry {
    if (country) {
        const c = country.toLowerCase().trim();
        if (c === 'brazil' || c === 'brasil') return 'brazil';
        if (c === 'canada') return 'canada';
        if (c === 'united-states' || c === 'united states' || c === 'usa' || c === 'us') return 'us';
    }
    if (currency) {
        const cur = currency.toUpperCase().trim();
        if (cur === 'BRL' || cur === 'REAL') return 'brazil';
        if (cur === 'CAD') return 'canada';
        if (cur === 'USD') return 'us';
    }
    // App is BR-first
    return 'brazil';
}

/** Return only the digit characters of a string. */
export function phoneDigits(raw: string): string {
    return raw.replace(/\D/g, '');
}

/**
 * Format a raw (possibly partially typed) phone string for the given country.
 * - brazil: 11 digits → (XX) XXXXX-XXXX  |  10 digits → (XX) XXXX-XXXX
 * - canada/us: 10 digits → (XXX) XXX-XXXX
 * Partial input is formatted progressively.
 * Returns the formatted string (original separators are stripped first).
 */
export function formatPhone(raw: string, country: PhoneCountry): string {
    const trimmed = (raw ?? '').trimStart();
    // International input (user typed a leading "+"): preserve their format so a
    // foreign client's number can be entered as-is — don't force a national
    // layout that would mangle it. Only strip characters that aren't phone-ish.
    if (trimmed.startsWith('+')) {
        return trimmed.replace(/[^\d+\-\s()]/g, '').slice(0, 20);
    }

    const maxDigits = country === 'brazil' ? 11 : 10;
    const digits = phoneDigits(raw).slice(0, maxDigits);

    if (country === 'brazil') {
        return formatBrazil(digits);
    }
    return formatNorthAmerica(digits);
}

function formatBrazil(digits: string): string {
    const len = digits.length;
    if (len === 0) return '';

    // (XX)
    if (len <= 2) return `(${digits}`;
    const area = digits.slice(0, 2);
    const rest = digits.slice(2);

    // The 9th digit (position index 2) tells us if it's 9-digit cell or 8-digit landline.
    // We determine format based on total length after the area code.
    // 9-digit cell: 9 remaining digits → (XX) XXXXX-XXXX
    // 8-digit landline: 8 remaining digits → (XX) XXXX-XXXX
    // While typing, use 9-digit layout once rest length > 8, otherwise 8-digit.
    const is9digit = rest.length > 8 || (rest.length === 9);

    if (is9digit) {
        // (XX) XXXXX-XXXX
        if (rest.length <= 5) return `(${area}) ${rest}`;
        return `(${area}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
    } else {
        // (XX) XXXX-XXXX
        if (rest.length <= 4) return `(${area}) ${rest}`;
        return `(${area}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    }
}

function formatNorthAmerica(digits: string): string {
    const len = digits.length;
    if (len === 0) return '';

    // (XXX)
    if (len <= 3) return `(${digits}`;
    const area = digits.slice(0, 3);
    const rest = digits.slice(3);

    // (XXX) XXX
    if (rest.length <= 3) return `(${area}) ${rest}`;

    // (XXX) XXX-XXXX
    return `(${area}) ${rest.slice(0, 3)}-${rest.slice(3)}`;
}

/**
 * True when the phone is empty (optional field), a valid national number, or a
 * plausible international one. We intentionally accept FOREIGN numbers regardless
 * of the business's own country, so a salon in Brazil can still store a client
 * with a US/Canada (or other) phone.
 * - international (leading "+"): 8–15 digits (E.164 range)
 * - national: 10 (US/CA or BR landline) or 11 (BR cell)
 *
 * `country` is kept for signature stability / formatting parity; validation is
 * deliberately country-agnostic on length so cross-border clients aren't blocked.
 */
export function isPhoneComplete(raw: string, _country: PhoneCountry): boolean {
    const trimmed = (raw ?? '').trim();
    if (trimmed.length === 0) return true; // empty = ok (phone is optional)
    const digits = phoneDigits(trimmed);
    if (trimmed.startsWith('+')) return digits.length >= 8 && digits.length <= 15;
    return digits.length === 10 || digits.length === 11;
}
